/**
 * The 1031 clock (VP8.1) - the two dates a 1031 buyer lives by, and how many days are left.
 *
 * WHAT IS A QUOTE AND WHAT IS A CLOCK. Every date here is carried by the contract
 * (`buyBox.exchange`), computed once by the portal's builder from the down-leg close date the
 * roster states, and overridable there by the accommodator's letter. Nothing is derived in the
 * browser except the COUNTDOWN, which is a clock rather than a figure: it has to move on its own
 * or it is wrong by the next morning. That split is why this file holds no arithmetic on money and
 * no knowledge of any particular exchange.
 *
 * Dates are handled at UTC midnight throughout. A `2026-09-30` parsed in a local timezone west of
 * Greenwich is the evening of the 29th, and the page would print the wrong day for half of America.
 */

export interface PortalExchange {
  label?: string;
  downLegClose?: string;
  identifyBy?: string;
  closeBy?: string;
  identifyDays?: number;
  closeDays?: number;
  dateSource?: string;
  basis?: string;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** A YYYY-MM-DD string as UTC midnight, or null when it is not one. */
export const utcDate = (iso: string | undefined | null): Date | null => {
  if (!iso || !ISO.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Today as YYYY-MM-DD in the READER'S OWN calendar - the calendar a countdown is read against. */
export const todayIso = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/** Whole days from `fromIso` to `toIso`; negative once the date is past. */
export const daysBetween = (fromIso: string, toIso: string): number | null => {
  const a = utcDate(fromIso);
  const b = utcDate(toIso);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};

/** "September 30, 2026". Formatted in UTC for the reason at the top of this file. */
export const formatDate = (iso: string | undefined | null): string | null => {
  const d = utcDate(iso);
  return d
    ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(d)
    : null;
};

/** The countdown, said plainly. A passed date says so rather than printing a negative number. */
export const countdownLabel = (days: number | null): string | null => {
  if (days === null) return null;
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} ${days === 1 ? 'day' : 'days'}`;
  return `${-days} ${days === -1 ? 'day' : 'days'} ago`;
};

export interface ExchangeRow {
  key: 'downLegClose' | 'identifyBy' | 'closeBy';
  label: string;
  date: string;
  countdown: string | null;
  /** What the date IS - "45 days after the down-leg closes". Never a countdown. */
  note: string | null;
  past: boolean;
}

/**
 * The three rows the page prints, in the order they happen. A row appears only when the contract
 * carries its date, so an exchange with no computed deadlines still renders the close date alone -
 * the same rule every other section of this portal follows.
 */
export const exchangeRows = (exchange: PortalExchange | null | undefined, today: string): ExchangeRow[] => {
  if (!exchange) return [];
  const rows: ExchangeRow[] = [];
  const push = (key: ExchangeRow['key'], label: string, iso: string | undefined, note: string | null) => {
    const date = formatDate(iso);
    if (!date || !iso) return;
    const days = daysBetween(today, iso);
    rows.push({ key, label, date, countdown: countdownLabel(days), note, past: days !== null && days < 0 });
  };
  const after = (n: number | undefined) =>
    typeof n === 'number' ? `${n} days after the down-leg closes` : null;
  push('downLegClose', 'Down-leg closes', exchange.downLegClose, 'scheduled');
  push('identifyBy', 'Identify replacement by', exchange.identifyBy, after(exchange.identifyDays));
  push('closeBy', 'Close on replacement by', exchange.closeBy, after(exchange.closeDays));
  return rows;
};
